import { useInternalNode, type EdgeProps } from "@xyflow/react";
import { useId, type SVGProps } from "react";
import type { Point } from "../model/cardGeometry";
import { pathBetween } from "../model/edgeRoutes";
import type { GraphEdgeKind } from "../model/workflowGraph";
import { ARROWHEAD, edgeClassName } from "./edgeKinds";
import type { PortFlowEdge } from "./flowElements";

/**
 * An edge drawn along the layout's route from its output port to its input port
 * (`edgeRoutes`). React Flow's own handle positions are ignored: it measures them once and
 * divides by its own zoom only, so under any other scaling they drift off the cards. While
 * the editor drags a card away from where the layout put it, the edge's end moves with the
 * card. The `data-` attributes name both ends for the browser check that ends meet their
 * ports.
 */
export function PortEdge({ id, data, source, target, sourceHandleId, targetHandleId }: EdgeProps<PortFlowEdge>) {
  const sourceAt = useInternalNode(source)?.internals.positionAbsolute;
  const targetAt = useInternalNode(target)?.internals.positionAbsolute;
  if (data === undefined) {
    return null;
  }
  const moved = (at: Point | undefined, origin: Point) => (at === undefined ? { x: 0, y: 0 } : { x: at.x - origin.x, y: at.y - origin.y });
  const sourceMoved = moved(sourceAt, data.sourceOrigin);
  const targetMoved = moved(targetAt, data.targetOrigin);
  const still = [sourceMoved, targetMoved].every((delta) => Math.abs(delta.x) < 0.5 && Math.abs(delta.y) < 0.5);
  const path = still
    ? data.route.path
    : pathBetween({ x: data.route.source.x + sourceMoved.x, y: data.route.source.y + sourceMoved.y }, { x: data.route.target.x + targetMoved.x, y: data.route.target.y + targetMoved.y });
  return (
    <EdgeLine
      id={id}
      d={path}
      kind={data.kind}
      waitsForRun={data.waitsForRun}
      data-source-node={source}
      data-source-port={sourceHandleId ?? undefined}
      data-target-node={target}
      data-target-port={targetHandleId ?? undefined}
    />
  );
}

/**
 * One edge's line and arrowhead, on the canvas and in the legend alike: its stroke by
 * kind, dotted when it leads into a step that waits for Run, and one arrowhead whose tip
 * sits where the line ends. The arrowhead is the line's own marker, so it follows the
 * line's direction and takes its colour.
 */
export function EdgeLine({ d, kind, waitsForRun, ...ends }: { d: string; kind: GraphEdgeKind; waitsForRun: boolean } & Omit<SVGProps<SVGPathElement>, "d" | "markerEnd">) {
  const marker = `app-flow-arrow-${useId().replace(/[^\w-]/g, "")}`;
  return (
    <g className={edgeClassName(kind, waitsForRun)}>
      <defs>
        <marker id={marker} viewBox="0 0 10 10" refX="10" refY="5" markerWidth={ARROWHEAD.length} markerHeight={ARROWHEAD.width} markerUnits="userSpaceOnUse" orient="auto-start-reverse">
          <path d="M0 0L10 5L0 10z" className="app-flow-arrowhead" />
        </marker>
      </defs>
      <path {...ends} className="app-flow-edge-line" d={d} fill="none" markerEnd={`url(#${marker})`} />
    </g>
  );
}
